import {Component} from 'panel';
import {apiClient} from 'common/util/api-client';
import {replaceByIndex} from 'common/util/array';
import {reporter} from 'common/report/error-reporting';

import template from './index.jade';
import css from './index.styl?inline';

const MIXPANEL_BANNER_EVENTS = {
  bannerShown: `Banner shown`,
  bannerHidden: `Banner hidden`,
};

class Banner {
  public type: string;
  public message: string;
  public name: string;
  public title: string;
  public visible: boolean;

  constructor(data) {
    this.type = data.type;
    this.message = data.message;
    this.name = data.name || data.title;
    this.title = data.title;
    this.visible = true;
  }

  hide() {
    this.visible = false;
    return this;
  }

  toEventData() {
    return {
      'banner type': this.type,
      'banner message': this.message,
      'banner name': this.name,
      'banner title': this.title,
    };
  }

  isValid() {
    return this.title && this.message && this.name;
  }
}

interface ReportBannersState {
  banners: Banner[];
}

class ReportBanners extends Component<ReportBannersState> {
  static get attrsSchema() {
    return {
      'project-id': `string`,
      'workspace-id': `number`,
      report: `string`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        banners: [],
      },
      helpers: {
        urlBase: () => {
          const projectId = this.attr(`project-id`);
          // TODO when workspaces
          // const workspaceId = this.attr(`workspace-id`);
          // const path = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
          const path = `projects/${projectId}`;

          return `/api/app/${path}`;
        },

        fetchBanners: async () => {
          const bannersUrl = `${this.helpers.urlBase()}/banners?report=${this.attr(`report`)}`;
          return await apiClient.get(bannersUrl);
        },

        updateBanners: (bannersJson) => {
          const banners = bannersJson.map((banner) => new Banner(banner)).filter((banner) => banner.isValid());

          if (banners.length) {
            mixpanel.track(`[Report Banner] Banners shown`, {
              banners: banners.map((banner) => banner.name),
            });
            banners.forEach((banner) => {
              this.helpers.trackBannerEvent(MIXPANEL_BANNER_EVENTS.bannerShown, banner);
            });
          }
          this.update({banners});
        },

        trackBannerEvent: (eventName, banner) => {
          if (mixpanel.banners) {
            mixpanel.banners.track(eventName, {
              ...banner.toEventData(),
              'report name': this.attr(`report`),
              'project id': this.attr(`project-id`),
              'workspace id': this.attr(`workspace-id`),
            });
          }
        },

        hideBanner: (idx) => {
          const {banners} = this.state;
          const banner = banners[idx].hide();
          this.update({banners: replaceByIndex(banners, idx, banner)});
          this.helpers.trackBannerEvent(MIXPANEL_BANNER_EVENTS.bannerHidden, banner);
        },
      },
    };
  }

  async connectedCallback() {
    super.connectedCallback();

    try {
      const banners = await this.helpers.fetchBanners();
      this.helpers.updateBanners(banners);
    } catch (err) {
      reporter.error(err);
    }
  }
}

customElements.define(`mp-report-banners`, ReportBanners);
